import type { ReactElement } from "react";
import { ChevronRight } from "lucide-react";
import { StatusPill, Tooltip } from "@playbookai/ui";
import type { DateTimeFormatter } from "../../../settings/format-date-time.js";
import { AdminTimeZoneLabel } from "../AdminTimeZoneLabel.js";
import {
  actionLabel,
  entityReference,
  entityTypeLabel,
  serviceLabel,
} from "../audit-format.js";

/** The Time column names its zone ONCE, in the header (PB-5216, d7): the
    Region preference's zone through `useDateTimeFormatter`, or the browser's
    detected zone until that preference is saved — and it says which, because
    "09:14" in a zone the reader did not choose is a different fact from the
    same digits in one they did. Both lists share this rule; the CSV keeps its
    own header, "Timestamp (UTC)". Reverses PB-5178 d10's host-local rendering.

    The wording and the stacked layout moved to `AdminTimeZoneLabel` in
    PB-5409, when the other sixteen admin timestamp surfaces adopted the same
    disclosure. This stays as the Audit table's name for it. */
export function timeHeader(formatter: DateTimeFormatter): ReactElement {
  return <AdminTimeZoneLabel label="Time" formatter={formatter} />;
}

/** The clock time only: the day is named once by the group header above the
    run, and the seconds are what order events within it. An unparseable value
    is shown as received rather than hidden. */
export function TimeCell({
  iso,
  formatter,
}: {
  iso: string;
  formatter: DateTimeFormatter;
}): ReactElement {
  return (
    <span className="pb-tabular whitespace-nowrap font-mono text-dense-1 text-pb-text-muted">
      {formatter.time(iso) || iso}
    </span>
  );
}

/** The row's trailing chevron: the keyboard's way into the drawer. The row
    itself answers a click (`onRowActivate`), but the table is not a keyboard
    grid here, so a focusable control in the row is what makes the drawer
    reachable without a pointer. */
export function DetailsButton({
  label,
  onOpen,
}: {
  label: string;
  onOpen: () => void;
}): ReactElement {
  /* The design-system Tooltip in place of the native title (d1). Its
     inline-flex wrapper moves the button's baseline and lifts it 2px in the
     row; align-middle (layout only) centres it again. */
  return (
    <Tooltip content="Event details" wrapperClassName="align-middle">
      {/* eslint-disable-next-line ds/no-hand-built -- kept: Button variant="ghost" iconOnly size="xs" is this 24px box, but it paints the glyph text-pb-text-secondary at rest instead of today's muted; no PR 3 look row approves that. */}
      <button
        type="button"
        aria-label={label}
        onClick={onOpen}
        className="rounded-md p-1 text-pb-text-muted hover:pb-overlay-paint hover:text-pb-text active:pb-overlay-paint-strong focus-ring"
      >
        <ChevronRight className="size-4" aria-hidden="true" />
      </button>
    </Tooltip>
  );
}

/** The readable action, and which part of the product wrote it. */
export function ActionCell({
  action,
  service,
}: {
  action: string;
  service?: string;
}): ReactElement {
  /* The readable label only. The machine code was shown beneath it so an
     auditor could quote the literal value; it is an internal identifier like
     any other, and it is no longer in the CSV export either — that carried an
     `Action code` column until the same rule was applied there. The service is
     mapped for the same reason: `playbook-be` says how the system is built,
     not what happened. */
  /* Wraps, and overrides the table's `nowrap`. This cell carries two elements
     and the table now sizes its columns from a declared share rather than from
     the widest row, so a long action beside a long service name has to fold
     onto a second line instead of running across the Affected column. */
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 whitespace-normal">
      <span className="font-medium text-pb-text">{actionLabel(action)}</span>
      {service ? (
        <StatusPill tone="info">{serviceLabel(service)}</StatusPill>
      ) : null}
    </span>
  );
}

/** The affected thing: its name where Auth can resolve one, its type where it
    cannot, and always a stable REFERENCE beneath. */
export function EntityCell({
  entityType,
  entityId,
  entityName,
}: {
  entityType: string;
  entityId: string;
  entityName?: string | null;
}): ReactElement {
  /* The reference is what makes two rows about DIFFERENT things tell apart.
     Names resolve only for entity types Auth owns, so most rows fall back to
     the type — and "Widget" against "Widget" distinguishes nothing. The local
     audit log really does hold two widgets both called "CR check" with
     identical payloads. The raw id still never reaches the DOM, so it cannot
     leak into a copied screenshot. */
  return (
    <span className="flex flex-col leading-tight">
      <span className="font-medium text-pb-text">
        {entityName ?? entityTypeLabel(entityType)}
      </span>
      <span className="text-dense-1 text-pb-text-muted">
        {entityName ? `${entityTypeLabel(entityType)} · ` : ""}
        <span className="pb-tabular">{entityReference(entityId)}</span>
      </span>
    </span>
  );
}

/** Who did it, as a person wherever the server could resolve one.
 *
 * Three genuinely different cases, and collapsing them would misreport:
 *   - no actor at all -> a system write (SCIM, deployment bootstrap);
 *   - actor resolved   -> their name, with the email beneath;
 *   - actor unresolved -> someone who has since left the tenant. The audit
 *     record outlives membership, so the id is all that honestly remains —
 *     inventing a name for a departed user would falsify the record. */
export function ActorCell({
  actorId,
  actorName,
  actorEmail,
}: {
  actorId: string | null;
  actorName: string | null;
  actorEmail: string | null;
}): ReactElement {
  if (!actorId) return <StatusPill tone="neutral">System</StatusPill>;
  if (!actorName) {
    /* "Former member" alone made every departed actor look like the same
       person: two people who both left were one indistinguishable value on a
       surface whose whole purpose is being accurate about who did what. The
       reference separates them, and is the same token the CSV export writes,
       so a row and its exported line match by eye — and it is not the id, so
       nothing internal reaches the reader. Actor identity is its own
       compliance fact, separate from the affected entity's. */
    return (
      <span className="text-pb-text-muted">
        Former member{" "}
        <span className="pb-tabular">· {entityReference(actorId)}</span>
      </span>
    );
  }
  return (
    <span className="flex flex-col leading-tight">
      <span className="font-medium text-pb-text">{actorName}</span>
      {actorEmail ? (
        <span className="text-dense-1 text-pb-text-muted">{actorEmail}</span>
      ) : null}
    </span>
  );
}
